/**
 * @fileoverview SMS Settings Panel component for managing auto-reply,
 * rate limits, and missed call SMS templates.
 */

import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, MessageSquare, Clock, AlertTriangle, Shield } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useSMSSettings } from '@/hooks/useMessages';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

/**
 * Panel for managing tenant SMS settings including auto-reply,
 * rate limits, and missed call templates.
 */
export function SMSSettingsPanel() {
  const { activeTenantId, isAdmin } = useAuth();
  const { data: settings, isLoading } = useSMSSettings();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Local form state
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [autoReplyTemplate, setAutoReplyTemplate] = useState('');
  const [missedCallSmsEnabled, setMissedCallSmsEnabled] = useState(true);
  const [missedCallTemplate, setMissedCallTemplate] = useState('');
  const [rateLimitPerHour, setRateLimitPerHour] = useState(3);
  const [rateLimitPerDay, setRateLimitPerDay] = useState(10);
  const [bookingUrl, setBookingUrl] = useState('');

  // Sync form state with fetched settings
  useEffect(() => {
    if (settings) {
      setAutoReplyEnabled(settings.sms_auto_reply_enabled ?? false);
      setAutoReplyTemplate(settings.sms_auto_reply_template ?? '');
      // Default to true - missed call SMS is enabled by default
      setMissedCallSmsEnabled(true);
      setMissedCallTemplate(settings.missed_call_sms_template ?? '');
      setRateLimitPerHour(settings.sms_rate_limit_per_hour ?? 3);
      setRateLimitPerDay(settings.sms_rate_limit_per_day ?? 10);
      setBookingUrl(settings.booking_url ?? '');
    }
  }, [settings]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!activeTenantId) throw new Error('No active tenant');

      const { error } = await supabase
        .from('calie_tenants')
        .update({
          sms_auto_reply_enabled: autoReplyEnabled,
          sms_auto_reply_template: autoReplyTemplate || null,
          missed_call_sms_template: missedCallTemplate || null,
          sms_rate_limit_per_hour: rateLimitPerHour,
          sms_rate_limit_per_day: rateLimitPerDay,
          booking_url: bookingUrl || null,
          updated_at: new Date().toISOString(),
        })
        .eq('tenant_id', activeTenantId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sms-settings', activeTenantId] });
      toast({
        title: 'Settings saved',
        description: 'Your SMS settings have been updated.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Error saving settings',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/4" />
            <div className="h-10 bg-muted rounded" />
            <div className="h-4 bg-muted rounded w-1/4" />
            <div className="h-20 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Auto-Reply Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Auto-Reply Settings
          </CardTitle>
          <CardDescription>
            Configure automatic SMS responses to incoming messages. Auto-reply is sent only once per contact within a 30-day period.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto-reply-enabled">Enable Auto-Reply</Label>
              <p className="text-sm text-muted-foreground">
                Automatically respond to incoming SMS messages
              </p>
            </div>
            <Switch
              id="auto-reply-enabled"
              checked={autoReplyEnabled}
              onCheckedChange={setAutoReplyEnabled}
            />
          </div>

          {autoReplyEnabled && (
            <div className="space-y-2">
              <Label htmlFor="auto-reply-template">Auto-Reply Template</Label>
              <Textarea
                id="auto-reply-template"
                placeholder="Thanks for your message! We'll get back to you shortly."
                value={autoReplyTemplate}
                onChange={(e) => setAutoReplyTemplate(e.target.value)}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                This message will be sent automatically when someone texts your tracking numbers.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Missed Call SMS Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Missed Call SMS
          </CardTitle>
          <CardDescription>
            Send automated SMS when calls are missed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="missed-call-enabled">Enable Missed Call SMS</Label>
              <p className="text-sm text-muted-foreground">
                Automatically send SMS when a call goes unanswered
              </p>
            </div>
            <Switch
              id="missed-call-enabled"
              checked={missedCallSmsEnabled}
              onCheckedChange={setMissedCallSmsEnabled}
            />
          </div>

          {missedCallSmsEnabled && (
            <>
              <div className="space-y-2">
                <Label htmlFor="missed-call-template">Missed Call Template</Label>
                <Textarea
                  id="missed-call-template"
                  placeholder="Sorry we missed your call! We'll call you back as soon as possible."
                  value={missedCallTemplate}
                  onChange={(e) => setMissedCallTemplate(e.target.value)}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  Available variables: {'{{booking_url}}'} - Your booking link
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="booking-url">Booking URL</Label>
                <Input
                  id="booking-url"
                  type="url"
                  placeholder="https://calendly.com/your-name"
                  value={bookingUrl}
                  onChange={(e) => setBookingUrl(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Optional link to your scheduling page (Calendly, Cal.com, etc.)
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Rate Limiting - Platform Admin Only */}
      {isAdmin?.() && (
        <Card className="border-purple-200 dark:border-purple-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-600" />
              Rate Limiting
              <Badge variant="outline" className="ml-2 text-purple-600 border-purple-300 bg-purple-50">
                Platform Admin
              </Badge>
            </CardTitle>
            <CardDescription>
              Control SMS sending limits for this tenant. This section is only visible to platform administrators.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rate-hour">Messages per Hour</Label>
                <Input
                  id="rate-hour"
                  type="number"
                  min={1}
                  max={1000}
                  value={rateLimitPerHour}
                  onChange={(e) => setRateLimitPerHour(parseInt(e.target.value) || 50)}
                />
                <p className="text-xs text-muted-foreground">
                  Max SMS per phone number per hour
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rate-day">Messages per Day</Label>
                <Input
                  id="rate-day"
                  type="number"
                  min={1}
                  max={10000}
                  value={rateLimitPerDay}
                  onChange={(e) => setRateLimitPerDay(parseInt(e.target.value) || 200)}
                />
                <p className="text-xs text-muted-foreground">
                  Max SMS per phone number per day
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
        >
          <Save className="h-4 w-4 mr-2" />
          {saveMutation.isPending ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}
